/**
 * Tusq · Display-only MED → M relabel (no product changes needed)
 * ================================================================
 * Shopify stores the size option value as "MED" in product variant data.
 * Renaming would require touching every product in admin (or a CSV import).
 * This script rewrites visible "MED" text to "M" in the shopper-facing UI —
 * variant option chips, size dropdowns, cart line variant labels, our own
 * color-picker size dropdown, and any other rendered variant text.
 *
 * The underlying variant IDs, option values, and cart data are untouched, so
 * filtering, adding to cart, and Shopify's own logic keep working with "MED".
 *
 * Approach:
 *   - Rewrite text on load
 *   - Run again whenever the DOM changes (MutationObserver) so async-rendered
 *     variant pickers, cart drawer updates, and Zoorix widgets are covered
 *   - Scoped word-boundary regex — never touches strings that just CONTAIN
 *     "MED" (e.g. "Media", "Recommended", or the header "MEDIA" nav link)
 */
(function () {
  'use strict';
  if (window.__tusqMedRelabelInit) return;
  window.__tusqMedRelabelInit = true;

  // Only rewrite in these places — safe list of variant/size UI containers.
  // Adding more selectors is safe (word-boundary regex won't touch prose).
  var SCOPE_SELECTOR = [
    // Native variant pickers
    'variant-selects', 'variant-radios',
    'label[for*="Option"]',
    'input[type="radio"][name*="option"] + label',
    'select[name*="option"]',
    'select[name*="options"]',
    'fieldset.product-form__input',
    // Cart line item variant text (Dawn native + Zoorix)
    '.cart-item__details', '.product-option', '.zrx-cart-item', '.cart-item',
    // Our color picker size dropdown
    '.tusq-color-picker__size',
    // Any element with data-option-value (Dawn 2.0 variant)
    '[data-option-value]',
    // Generic — anything explicitly tagged as size UI
    '[data-tusq-size-relabel]'
  ].join(', ');

  // Word-boundary regex → matches only the literal token "MED".
  // Case-insensitive to catch "Med", "med" etc. rendered by Shopify.
  var MED_RE = /\bMED\b/gi;

  function rewriteTextNode(node) {
    var t = node.nodeValue;
    if (!t || t.indexOf('M') === -1) return;
    if (!MED_RE.test(t)) return;
    MED_RE.lastIndex = 0;
    node.nodeValue = t.replace(MED_RE, 'M');
  }

  // NodeFilter that rejects text inside <script>, <style>, JSON blobs, and
  // any [type="application/json"] elements. Critical: <variant-selects> in
  // Dawn contains a serialized product-json blob inside a <script> tag; if we
  // rewrite MED→M in that JSON, the variant lookup breaks and the ATC button
  // renders as "Sold out" for every variant. This filter keeps us safe.
  var SKIP_TAGS = { SCRIPT: 1, STYLE: 1, NOSCRIPT: 1, TEMPLATE: 1 };
  var textFilter = {
    acceptNode: function (node) {
      var el = node.parentElement;
      while (el) {
        if (SKIP_TAGS[el.tagName]) return NodeFilter.FILTER_REJECT;
        var type = el.getAttribute && el.getAttribute('type');
        if (type && /json|ld\+json/i.test(type)) return NodeFilter.FILTER_REJECT;
        el = el.parentElement;
      }
      return NodeFilter.FILTER_ACCEPT;
    }
  };

  function rewriteWithinElement(el) {
    if (!el) return;
    // Rewrite text nodes inside `el`, skipping any inside <script>/<style>/JSON blobs
    var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, textFilter, false);
    var n;
    while ((n = walker.nextNode())) {
      rewriteTextNode(n);
    }
    // Rewrite <option> label text ONLY (never the .value attribute). Options don't
    // sit inside script tags, so this is safe.
    var options = el.tagName === 'OPTION' ? [el] : el.querySelectorAll ? el.querySelectorAll('option') : [];
    Array.prototype.forEach.call(options, function (opt) {
      if (opt.textContent && MED_RE.test(opt.textContent)) {
        MED_RE.lastIndex = 0;
        opt.textContent = opt.textContent.replace(MED_RE, 'M');
      }
    });
  }

  function relabelPage() {
    // Match all scoped containers + also do a shallow pass through cart/PDP core areas
    var containers = document.querySelectorAll(SCOPE_SELECTOR);
    Array.prototype.forEach.call(containers, rewriteWithinElement);
    // Cover fresh option-value spans that Dawn renders as buttons (`.pill` / `.color-swatch`)
    Array.prototype.forEach.call(document.querySelectorAll('[data-option-value-id]'), rewriteWithinElement);
  }

  // Debounced trigger — coalesce mutations into a single pass
  var pending = null;
  function schedule() {
    if (pending) return;
    pending = setTimeout(function () { pending = null; relabelPage(); }, 50);
  }

  function start() {
    relabelPage();
    var mo = new MutationObserver(schedule);
    mo.observe(document.body, { childList: true, subtree: true, characterData: true });
    // Also relabel on cart events
    document.addEventListener('cart:updated', schedule);
    document.addEventListener('cart:refresh', schedule);
    document.addEventListener('variant:change', schedule);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
